// Разбор search-параметров адреса, который не роняет страницу из-за одного поля.
//
// Почему понадобился. TanStack Router по умолчанию прогоняет КАЖДОЕ значение
// адреса через `JSON.parse`: `?plan=1` приезжает числом 1, `?q=2024` — числом,
// `?create=true` — булевым, `?q=null` — `null`, а единственный `?plan=abc` —
// строкой там, где схема ждёт массив. Голая Zod-схема (`z.string()`,
// `z.boolean()`, `z.enum`, `z.uuid()`) на таком значении проваливается, роутер
// бросает `SearchParamError`, и человек получает экран ошибки вместо страницы —
// из-за одного мусорного параметра. Внутренние ссылки этим не страдают (роутер
// сам берёт в кавычки строки, похожие на JSON), ломаются внешние: письма,
// лендинг, боты, адреса, набранные руками. Класс уже стоил четырёх точечных
// заплаток в разных роутах и события в Hawk на каждую новую.
//
// Как устроено. Схема роута остаётся обычной Zod-схемой — с её типами для
// `<Link search>` и `useSearch()`. Обёртка разбирает её ПОЛЕ ЗА ПОЛЕМ:
//   1. значение как есть;
//   2. то же значение в форме, которую мог исказить JSON-разбор роутера
//      (число/булево → строка, «1»/«true» → булево, строка-число → число,
//      одиночное значение → массив из одного элемента);
//   3. отсутствие значения — `.optional()` даёт `undefined`, `.default()` —
//      значение по умолчанию.
// Первое подошедшее побеждает. Плохой параметр отбрасывается, страница
// открывается. Бросает обёртка только на поле, у которого нет ни значения, ни
// умолчания, — без него страница и правда не построится.
//
// Почему не свой `parseSearch` в `createRouter`. Он меняет разбор адреса всем
// роутам разом, а enum/uuid/regex по-прежнему роняли бы страницу на любом
// непредусмотренном значении. Поле за полем закрывает весь класс.
//
// Цена: число, набранное в адресе, роутер уже разобрал — `?q=1e3` вернётся
// строкой «1000», `?q=1.0` — «1», цифры длиннее 2^53 округлятся. Для поисковых
// строк и идентификаторов из внешних ссылок это приемлемо; внутренние ссылки
// роутер кодирует без потерь.

import { z } from "zod";

/**
 * Форма валидатора, которую TanStack Router понимает как адаптер
 * (`ValidatorAdapter`): типы входа и выхода берутся из `types`, разбор —
 * `parse`. Описана структурно, чтобы `@studyhub/shared` не зависел от роутера.
 * Поле `types` обязательно: без него роутер выводит тип ссылок из `parse`, и
 * поля с `.default()` стали бы обязательными в `<Link search>`.
 */
export interface TolerantSearchValidator<Input, Output> {
	readonly types: { readonly input: Input; readonly output: Output };
	parse(input: unknown): Output;
}

const NO_VALUE: unique symbol = Symbol("no-value");

function scalarToString(value: unknown): string | undefined {
	if (typeof value === "string") return value;
	if (typeof value === "number") return Number.isFinite(value) ? String(value) : undefined;
	if (typeof value === "boolean" || value === null) return String(value);
	return undefined;
}

/** Формы значения, которые мог исказить JSON-разбор адреса, — по убыванию естественности. */
function recoveryCandidates(raw: unknown): unknown[] {
	if (Array.isArray(raw)) {
		const texts = raw.map(scalarToString);
		return texts.every((item) => item !== undefined) ? [texts] : [];
	}
	const text = scalarToString(raw);
	if (text === undefined) return [];
	const candidates: unknown[] = [];
	if (typeof raw !== "string") candidates.push(text);
	if (text === "true" || text === "1") candidates.push(true);
	if (text === "false" || text === "0") candidates.push(false);
	const numeric = Number(text);
	if (typeof raw !== "number" && text.trim() !== "" && Number.isFinite(numeric)) {
		candidates.push(numeric);
	}
	// Единственное значение там, где ждут список: сначала как есть, потом строкой.
	// Пустое (`?plan=`) списком не становится — это «не задано», а не «один пустой».
	if (text !== "") candidates.push([raw], [text]);
	return candidates;
}

function tryParse(field: z.core.$ZodType, value: unknown): unknown {
	try {
		const result = z.safeParse(field, value);
		return result.success ? result.data : NO_VALUE;
	} catch {
		// `.transform` поля может бросить не Zod-ошибкой — для нас это тот же отказ.
		return NO_VALUE;
	}
}

function parseField(field: z.core.$ZodType, raw: unknown, present: boolean): unknown {
	if (present) {
		const direct = tryParse(field, raw);
		if (direct !== NO_VALUE) return direct;
		for (const candidate of recoveryCandidates(raw)) {
			const recovered = tryParse(field, candidate);
			if (recovered !== NO_VALUE) return recovered;
		}
	}
	return tryParse(field, undefined);
}

/**
 * Обернуть Zod-схему search-параметров роута: типы те же, что у схемы, а
 * разбор не роняет страницу из-за одного плохого поля (см. шапку файла).
 *
 * ```ts
 * export const Route = createFileRoute("/catalog")({
 *   validateSearch: tolerantSearch(searchSchema),
 * });
 * ```
 */
export function tolerantSearch<Schema extends z.ZodObject>(
	schema: Schema,
): TolerantSearchValidator<z.input<Schema>, z.output<Schema>> {
	// Разбор идёт по полям, поэтому проверки уровня объекта (`.refine` /
	// `.superRefine` / `.check` на всей схеме) он бы молча пропустил. Таких схем
	// у роутов нет; появится — пусть упадёт при загрузке модуля, а не в проде.
	if (schema._zod.def.checks?.length) {
		throw new Error(
			"tolerantSearch: проверки уровня объекта не поддерживаются — перенеси их в поля",
		);
	}
	const fields = Object.entries(schema.shape) as [string, z.core.$ZodType][];
	return {
		types: undefined as unknown as { input: z.input<Schema>; output: z.output<Schema> },
		parse(input: unknown): z.output<Schema> {
			const source =
				input !== null && typeof input === "object" ? (input as Record<string, unknown>) : {};
			const result: Record<string, unknown> = {};
			const unresolved: string[] = [];
			for (const [key, field] of fields) {
				const present = Object.hasOwn(source, key);
				const value = parseField(field, source[key], present);
				if (value === NO_VALUE) {
					unresolved.push(key);
					continue;
				}
				// Ключ, пришедший в адресе, пишем даже со значением `undefined`:
				// роутер накладывает результат поверх сырых параметров, и без ключа
				// отброшенное значение «просочилось» бы в `useSearch()` как есть.
				if (value !== undefined || present) result[key] = value;
			}
			if (unresolved.length > 0) {
				throw new Error(`search: обязательные параметры не разобраны — ${unresolved.join(", ")}`);
			}
			return result as unknown as z.output<Schema>;
		},
	};
}
